import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

const SessionTimeoutModal = ({ open, onStayLoggedIn, onLogout, remainingTime }) => {
  const theme = useTheme();
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    if (open) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [open, onLogout]);

  return (
    <Modal
      open={open}
      aria-labelledby="session-timeout-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 400,
          p: 3,
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 32 }} />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Session Timeout Warning
          </Typography>

          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Your session will expire in {countdown} seconds due to inactivity.
          </Typography>

          <Box sx={{ position: 'relative', display: 'inline-flex', my: 2 }}>
            <CircularProgress
              variant="determinate"
              value={(countdown / 60) * 100}
              size={80}
              thickness={4}
              sx={{ color: theme.palette.warning.main }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                {countdown}s
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Button
              variant="outlined"
              onClick={onLogout}
              fullWidth
              sx={{
                borderColor: theme.palette.error.main,
                color: theme.palette.error.main,
                '&:hover': {
                  borderColor: theme.palette.error.dark,
                  bgcolor: alpha(theme.palette.error.main, 0.04),
                },
              }}
            >
              Logout Now
            </Button>
            <Button
              variant="contained"
              onClick={onStayLoggedIn}
              fullWidth
              sx={{
                bgcolor: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              Stay Logged In
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default SessionTimeoutModal; 